/**
 * Admin Funnel Dashboard — money flow + onboarding funnel metrics.
 * Visible only for admin users.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Clock, Users, Zap, Eye, Send, Inbox, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import PanelLayout from '@/components/PanelLayout';
import { API } from '@/utils/api';

export default function AdminFunnel() {
  const [data, setData] = useState(null);
  const [onboardingStats, setOnboardingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/funnel`, { headers }).then(r => r.data).catch(() => null),
      axios.get(`${API}/analytics/funnel/stats/onboarding?days=30`, { headers }).then(r => r.data).catch(() => null),
    ]).then(([funnelData, obStats]) => {
      setData(funnelData);
      setOnboardingStats(obStats);
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  if (loading) return <PanelLayout><div className="flex items-center justify-center h-64 text-zinc-400">Ladowanie...</div></PanelLayout>;
  if (!data) return <PanelLayout><div className="flex items-center justify-center h-64 text-zinc-400">Brak dostepu (tylko admin)</div></PanelLayout>;

  const { counts, kpis, funnel } = data;
  const maxFunnel = Math.max(...funnel.map(f => f.count), 1);

  return (
    <PanelLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-zinc-900 text-white text-xs font-semibold mb-3">Admin</span>
          <h1 className="text-2xl font-bold text-zinc-900 mb-1">Money Flow Dashboard</h1>
          <p className="text-sm text-zinc-500">Ostatnie 30 dni</p>
        </div>

        {/* Onboarding Funnel Section */}
        {onboardingStats && (
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-8" data-testid="onboarding-funnel">
            <button
              onClick={() => setShowOnboarding(!showOnboarding)}
              className="w-full flex items-center justify-between mb-4"
            >
              <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#F47A3B]" /> Onboarding Funnel
                <span className="text-sm font-normal text-zinc-400">
                  ({onboardingStats.total_starts} sesji, {onboardingStats.completion_rate}% ukonczone)
                </span>
              </h2>
              {showOnboarding ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </button>

            {showOnboarding && (
              <>
                {/* Onboarding KPIs */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-zinc-50 rounded-xl p-3">
                    <p className="text-[11px] text-zinc-500 font-medium mb-1">Rozpoczecia</p>
                    <p className="text-2xl font-bold text-zinc-800">{onboardingStats.total_starts}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-xl p-3">
                    <p className="text-[11px] text-zinc-500 font-medium mb-1">Ukonczenia</p>
                    <p className="text-2xl font-bold text-emerald-600">{onboardingStats.total_completes}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-xl p-3">
                    <p className="text-[11px] text-zinc-500 font-medium mb-1">Czas (sredni)</p>
                    <p className="text-2xl font-bold text-zinc-800">
                      {onboardingStats.avg_time_to_complete
                        ? `${Math.round(onboardingStats.avg_time_to_complete / 60)}min`
                        : '--'}
                    </p>
                  </div>
                </div>

                {/* Step-by-step funnel */}
                {onboardingStats.steps && onboardingStats.steps.length > 0 && (
                  <div className="space-y-3">
                    {onboardingStats.steps.map((step, i) => {
                      const maxUsers = Math.max(...onboardingStats.steps.map(s => s.users), 1);
                      const pct = (step.users / maxUsers) * 100;
                      return (
                        <div key={step.step} data-testid={`ob-funnel-step-${i}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[12px] font-medium text-zinc-600 truncate max-w-[200px]">
                              {step.step_name || step.step}
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-[12px] text-zinc-500">{step.users} users</span>
                              <span className="text-[12px] font-bold text-zinc-800">{step.rate}%</span>
                              {step.drop_off_rate > 20 && (
                                <span className="flex items-center gap-1 text-[11px] text-red-400 font-medium">
                                  <AlertTriangle className="w-3 h-3" />
                                  -{step.drop_off_rate}%
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="h-5 bg-zinc-100 rounded-lg overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.6, delay: i * 0.05 }}
                              className="h-full rounded-lg"
                              style={{
                                background: step.drop_off_rate > 30
                                  ? 'linear-gradient(90deg, #EF4444, #F87171)'
                                  : `linear-gradient(90deg, #F47A3B, ${step.step === 'complete' ? '#10B981' : '#F9A66C'})`,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Drop-off hot spots */}
                {onboardingStats.drop_off_points && onboardingStats.drop_off_points.length > 0 && (
                  <div className="mt-5 p-4 bg-red-50 rounded-xl border border-red-100">
                    <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Krytyczne drop-offy
                    </h3>
                    <div className="space-y-1">
                      {onboardingStats.drop_off_points.map((dp, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-red-600">
                            {dp.from_step} &rarr; {dp.to_step}
                          </span>
                          <span className="font-bold text-red-700">-{dp.drop_off_rate}% ({dp.users_lost} users)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'CTR Notyfikacji', value: `${kpis.ctr_notif}%`, icon: Eye, color: kpis.ctr_notif >= 30 ? 'emerald' : 'amber' },
            { label: 'Publish Rate', value: `${kpis.publish_rate}%`, icon: Send, color: kpis.publish_rate >= 40 ? 'emerald' : 'red' },
            { label: 'Time to Publish', value: kpis.time_to_publish_sec > 0 ? `${Math.round(kpis.time_to_publish_sec / 60)}min` : '--', icon: Clock, color: kpis.time_to_publish_sec < 120 ? 'emerald' : 'amber' },
            { label: 'Lead Rate', value: `${kpis.lead_rate}%`, icon: Inbox, color: kpis.lead_rate > 0 ? 'emerald' : 'zinc' },
          ].map((kpi, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white border border-zinc-200 rounded-2xl p-4"
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon className={`w-4 h-4 text-${kpi.color}-500`} />
                <span className="text-[11px] text-zinc-500 font-medium">{kpi.label}</span>
              </div>
              <p className={`text-2xl font-bold text-${kpi.color}-600`}>{kpi.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Funnel */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-8" data-testid="funnel-view">
          <h2 className="text-lg font-bold text-zinc-900 mb-5 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#F47A3B]" /> Money Flow Funnel
          </h2>
          <div className="space-y-4">
            {funnel.map((step, i) => {
              const pct = maxFunnel > 0 ? (step.count / maxFunnel * 100) : 0;
              const dropoff = i > 0 && funnel[i - 1].count > 0
                ? Math.round((1 - step.count / funnel[i - 1].count) * 100)
                : 0;
              return (
                <div key={step.step} data-testid={`funnel-step-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-medium text-zinc-700">{step.step}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-[12px] text-zinc-500">{step.users} users</span>
                      <span className="text-[14px] font-bold text-zinc-900">{step.count}</span>
                      {dropoff > 0 && (
                        <span className="text-[11px] text-red-400 font-medium">-{dropoff}%</span>
                      )}
                    </div>
                  </div>
                  <div className="h-6 bg-zinc-100 rounded-lg overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.6, delay: i * 0.1 }}
                      className="h-full rounded-lg"
                      style={{ background: `linear-gradient(90deg, #F47A3B, ${i === funnel.length - 1 ? '#10B981' : '#F9A66C'})` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Raw event counts */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="raw-events">
          <h2 className="text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#F47A3B]" /> Wszystkie eventy
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(counts).map(([key, val]) => (
              <div key={key} className="bg-zinc-50 rounded-xl p-3">
                <p className="text-[11px] text-zinc-500 font-medium mb-1">{key}</p>
                <p className="text-lg font-bold text-zinc-800">{val}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}
